import { Zap } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ClampText } from "components/common/ClampText";
import type { ShortestPathPanelProps } from "./types";

export const ShortestPathPanel = ({
  cablePathState,
  isCalculatingShortestPath = false,
  isObjectSelected = false,
  onFindShortestPath,
  onClearRoute,
}: ShortestPathPanelProps) => {
  const { t } = useTranslation();

  const pathData = cablePathState.shortestPath;
  const cableInfo = cablePathState.cableInfo;

  const hasPath = Boolean(pathData && pathData.waypoints.length > 0);

  const breakdown = pathData?.distanceBreakdown;

  return (
    <div className="space-y-2">
      {/* Circuit Information Section - Shows if cableInfo exists */}
      {cableInfo && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800 space-y-2">
          <div className="mb-3 pb-1 border-b border-blue-200">
            <div className="text-base font-semibold text-blue-900">
              {t("shortestPath.cableInformation")}
            </div>
          </div>
          {cableInfo.circuit && (
            <div>
              <span className="font-medium">
                {t("shortestPath.circuit.circuit")}:
              </span>{" "}
              {cableInfo.circuit}
            </div>
          )}
          {cableInfo.breaker && (
            <div>
              <span className="font-medium">
                {t("shortestPath.circuit.breaker")}:
              </span>{" "}
              {cableInfo.breaker}
            </div>
          )}
          {cableInfo.cableSpec && (
            <div>
              <span className="font-medium">
                {t("shortestPath.circuit.cableSpec")}:
              </span>{" "}
              {cableInfo.cableSpec}
            </div>
          )}
          {cableInfo.cableType && (
            <div>
              <span className="font-medium">
                {t("shortestPath.circuit.cableType")}:
              </span>{" "}
              {cableInfo.cableType}
            </div>
          )}
          {cableInfo.destination && (
            <div>
              <span className="font-medium">
                {t("shortestPath.circuit.destination")}:
              </span>{" "}
              {cableInfo.destination}
            </div>
          )}
          {cableInfo.sourceName && (
            <div>
              <span className="font-medium">
                {t("shortestPath.circuit.source")}:
              </span>{" "}
              {cableInfo.sourceName}
            </div>
          )}
          {cableInfo.cableReasoning && (
            <div className="flex flex-col">
              <span className="font-medium">
                {t("shortestPath.circuit.cableReasoning")}:
              </span>{" "}
              <ClampText text={cableInfo.cableReasoning} lines={3} />
            </div>
          )}
        </div>
      )}
      <button
        onClick={hasPath ? onClearRoute : onFindShortestPath}
        disabled={isCalculatingShortestPath || !isObjectSelected}
        className="w-full px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
        title={
          !isObjectSelected
            ? t("shortestPath.selectObjectFirst")
            : hasPath
              ? t("shortestPath.hideShortestPath")
              : t("shortestPath.showShortestPath")
        }
      >
        <Zap size={16} />
        {isCalculatingShortestPath
          ? t("shortestPath.calculating")
          : hasPath
            ? t("shortestPath.hideShortestPath")
            : t("shortestPath.showShortestPath")}
      </button>
      {/* Distance Breakdown Section - Shows if breakdown exists */}
      {hasPath && breakdown && (
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800 space-y-2">
          {/* Cable Length Information */}
          <div className="space-y-1">
            <div>
              <span className="font-medium">
                {t("shortestPath.distance.totalCable")}:
              </span>{" "}
              {breakdown.totalMeters.toFixed(2)}{" "}
              {t("shortestPath.distance.meters")}
            </div>
            <div className="text-xs opacity-90">
              {t("shortestPath.distance.tray")}:{" "}
              {breakdown.trayMeters.toFixed(2)}{" "}
              {t("shortestPath.distance.meters")} +{" "}
              {t("shortestPath.distance.vertical")}:{" "}
              {breakdown.verticalMeters.toFixed(2)}{" "}
              {t("shortestPath.distance.meters")}
            </div>
          </div>
          {/* Destinations */}
          {cableInfo?.destinations && (
            <div>
              <span className="font-medium">
                {t("shortestPath.circuit.destinations")}:
              </span>{" "}
              {cableInfo.destinations.reachable}/{cableInfo.destinations.total}{" "}
              {t("shortestPath.circuit.reachable")}
            </div>
          )}
          {/* Shared Trunk */}
          {cableInfo?.sharedTrunkPercentage !== undefined && (
            <div>
              <span className="font-medium">
                {t("shortestPath.circuit.sharedTrunk")}:
              </span>{" "}
              {cableInfo.sharedTrunkPercentage}%
            </div>
          )}
          <button
            onClick={onClearRoute}
            className="mt-2 w-full px-2 py-1 text-xs bg-yellow-200 hover:bg-yellow-300 rounded transition-colors"
          >
            {t("shortestPath.clearRoute")}
          </button>
        </div>
      )}
    </div>
  );
};
